import { useEffect, useState } from 'react';

import { useNavigate, useSearchParams } from 'react-router-dom';

import { Center, Flex, Spinner } from '@chakra-ui/react';

import { PaginationState, RowSelectionState, SortingState } from '@tanstack/react-table';

import {
  DeductionsReportStatus,
  DropdownOption,
  FilterData,
  Filters,
  MultiSelectionData,
  useToast,
} from '@aslanpay/portal-common-lib';

import DeductionsReportErrorsModal from 'app/components/DeductionReports/DeductionsReportErrorsModal';
import RetryDeductionsReportDialog from 'app/components/DeductionReports/RetryDeductionsReportDialog';
import { Table } from 'app/components/UI/Table/Table';
import { DeductionsReportRow } from 'app/components/UI/Table/Table.types';

import { defaultPageSize } from 'app/constants/table';

import useDownloadDeductionsReport from 'app/hooks/useDownloadDeductionsReport';
import useDownloadDeductionsReports from 'app/hooks/useDownloadDeductionsReports';
import useFetchDeductionsReportsPage from 'app/hooks/useFetchDeductionsReportsPage';
import useFetchCompanyIntegrations from 'app/hooks/useFetchCompanyIntegrations';
import useFetchPayCalendars from 'app/hooks/useFetchPayCalendars';
import useResubmitDeductionsReport from 'app/hooks/useResubmitDeductionsReport';
import useRetryDeductionsReport from 'app/hooks/useRetryDeductionsReport';

import DeductionsReportsStatusValues from 'app/constants/deductionsReports';

import { useManagingCompanyStore, useNavigationStore } from 'app/store';

import getDeductionsReportColumns from 'app/utils/table/deductionsReports';
import { getDeductionsReportProfilePath } from 'app/utils/routes';

import type { Filter } from '@aslanpay/portal-common-lib';

const PAGE_SIZE_OPTIONS = [
  { value: 15, text: '15 rows / page' },
  { value: 25, text: '25 rows / page' },
  { value: 50, text: '50 rows / page' },
];
const STATUS_FILTER = 'Report status';
const PAY_CALENDAR_FILTER = 'Pay calendar';
const COMPANY_REPORT_SORT_FIELDS = [
  'id',
  'salaryDeductions',
  'totalRecords',
  'status',
  'createdAt',
  'updatedAt',
];

interface CompanyDeductionReportsTabProps {
  payCalendarId?: string;
  companyId?: string;
}

/**
 * Lists company deduction reports: every company's, the managed company's, or, given a pay calendar, only that calendar's.
 */
function CompanyDeductionReportsTab({
  payCalendarId = undefined,
  companyId = undefined,
}: CompanyDeductionReportsTabProps) {
  const toast = useToast();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const company = useManagingCompanyStore((state) => state.company);
  const view = useNavigationStore((state) => state.view);
  const isPayCalendarScoped = Boolean(payCalendarId);
  const isReportsQueryEnabled = isPayCalendarScoped || view === 'global' || Boolean(company?.id);
  const isCompanyView = !isPayCalendarScoped && view === 'company' && Boolean(company?.id);

  const { data: integrations } = useFetchCompanyIntegrations(companyId ?? company?.id);

  const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: defaultPageSize,
  });
  const [statusFilter, setStatusFilter] = useState<DeductionsReportStatus[]>(() =>
    searchParams.get('status')
      ? (searchParams.get('status')?.split(',') as DeductionsReportStatus[])
      : []
  );
  const [payCalendarFilter, setPayCalendarFilter] = useState<string[]>(
    () => searchParams.get('payCalendarIds')?.split(',') ?? []
  );
  const [activeFilter, setActiveFilter] = useState<string | undefined>();
  const initialSortBy = searchParams.get('sortBy');
  const [sorting, setSorting] = useState<SortingState>(() => [
    {
      id:
        initialSortBy && COMPANY_REPORT_SORT_FIELDS.includes(initialSortBy)
          ? initialSortBy
          : 'createdAt',
      desc: searchParams.get('sort') !== 'ASC',
    },
  ]);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [reportIdPendingRetry, setReportIdPendingRetry] = useState<string | undefined>();
  const [reportWithProblemsShown, setReportWithProblemsShown] = useState<
    DeductionsReportRow | undefined
  >();

  const statusOptions: DropdownOption[] = Object.entries(DeductionsReportsStatusValues).map(
    ([value, { value: text }]) => ({ value, text })
  );
  const { data: payCalendars } = useFetchPayCalendars(
    { companyId: company?.id ? [company.id] : undefined, page: 0, size: 999999 },
    isCompanyView
  );
  const payCalendarOptions: DropdownOption[] = (payCalendars?.list ?? [])
    .map((payCalendar) => ({ value: payCalendar.id, text: payCalendar.name }))
    .sort((left, right) => left.text.localeCompare(right.text));
  const filters: Filter[] = [
    {
      name: STATUS_FILTER,
      type: 'category',
      data: statusOptions,
      selectedValues: statusFilter,
    },
    ...(isCompanyView
      ? [
          {
            name: PAY_CALENDAR_FILTER,
            type: 'category',
            data: payCalendarOptions,
            selectedValues: payCalendarFilter,
          } as Filter,
        ]
      : []),
  ];

  const chosenPayCalendarIds =
    isCompanyView && payCalendarFilter.length > 0 ? payCalendarFilter : undefined;

  const { data: deductionsReports, isLoading } = useFetchDeductionsReportsPage(
    {
      companyIds: isCompanyView && company?.id ? [company.id] : undefined,
      payCalendarIds: payCalendarId ? [payCalendarId] : chosenPayCalendarIds,
      status: statusFilter,
      sortBy: sorting[0]?.id,
      sort: !sorting[0] || sorting[0]?.desc ? 'DESC' : 'ASC',
      page: pageIndex,
      size: pageSize,
    },
    isReportsQueryEnabled
  );

  const { mutate: downloadDeductionsReport } = useDownloadDeductionsReport({
    onSuccess: () => {
      toast({ position: 'top-right', status: 'success', title: 'Report downloaded' });
    },
    onError: () => {
      toast({
        position: 'top-right',
        status: 'error',
        title: 'Something went wrong',
        description: 'Sorry, it was not possible to download the file. Please try again.',
      });
    },
  });

  const { mutate: downloadDeductionsReports } = useDownloadDeductionsReports({
    onSuccess: (_, { ids }) => {
      toast({
        position: 'top-right',
        status: 'success',
        title: `${ids.length} reports downloaded`,
      });
    },
    onError: () => {
      toast({
        position: 'top-right',
        status: 'error',
        title: 'Something went wrong',
        description: 'Sorry, it was not possible to download the files. Please try again.',
      });
    },
  });

  const {
    mutate: resubmitDeductionsReport,
    isPending: isResubmittingDeductionsReport,
    variables: resubmissionVariables,
  } = useResubmitDeductionsReport();

  const { mutate: retryDeductionsReport, isPending: isRetryingDeductionsReport } =
    useRetryDeductionsReport();

  const handleConfirmRetry = () => {
    if (!reportIdPendingRetry) {
      return;
    }

    retryDeductionsReport(
      { id: reportIdPendingRetry },
      { onSettled: () => setReportIdPendingRetry(undefined) }
    );
  };

  const handleDownloadReports = (ids: string[]) => {
    downloadDeductionsReports({ ids: [...new Set(ids)] });
    setRowSelection({});
  };

  const handleFilterChange = (name: string, values: (string | number)[]) => {
    if (name === PAY_CALENDAR_FILTER) {
      setPayCalendarFilter(values as string[]);
    } else {
      setStatusFilter(values as DeductionsReportStatus[]);
    }
    setPagination({ pageIndex: 0, pageSize: defaultPageSize });
  };

  const handleSelect = (report: DeductionsReportRow) => {
    navigate(getDeductionsReportProfilePath(report.id));
  };

  const formatFilterValue = (_: string, filterValue: string | number, filterData: FilterData) => {
    return (
      (filterData as MultiSelectionData).find((option) => option.value === filterValue)?.text ||
      String(filterValue)
    );
  };

  useEffect(() => {
    const params = new URLSearchParams(searchParams);

    params.set('page', `${pageIndex + 1}`);
    params.set('sortBy', sorting[0]?.id || 'createdAt');
    params.set('sort', sorting[0]?.desc ? 'DESC' : 'ASC');

    if (statusFilter.length > 0) {
      params.set('status', statusFilter.join(','));
    } else {
      params.delete('status');
    }

    if (payCalendarFilter.length > 0) {
      params.set('payCalendarIds', payCalendarFilter.join(','));
    } else {
      params.delete('payCalendarIds');
    }

    if (params.toString() !== searchParams.toString()) {
      setSearchParams(params, { replace: true });
    }
  }, [pageIndex, payCalendarFilter, searchParams, setSearchParams, sorting, statusFilter]);

  const actions = {
    select: handleSelect,
    download: (id: string) => downloadDeductionsReport({ id }),
    resubmit: (deductionsReportId: string) =>
      resubmitDeductionsReport(
        { deductionsReportId },
        {
          onSuccess: () => {
            toast({
              position: 'top-right',
              status: 'success',
              title: 'Report resubmitted',
            });
          },
          onError: () => {
            toast({
              position: 'top-right',
              status: 'error',
              title: 'Something went wrong',
              description: 'Sorry, the report could not be resubmitted. Please try again.',
            });
          },
        }
      ),
    isResubmitting: isResubmittingDeductionsReport,
    resubmittingDeductionsReportId: resubmissionVariables?.deductionsReportId,
    retry: setReportIdPendingRetry,
    isRetrying: isRetryingDeductionsReport,
    viewProblems: setReportWithProblemsShown,
  };

  const hasActivePayrollOutputIntegration =
    integrations?.some(
      (integration) =>
        integration.status === 'ACTIVE' &&
        integration.types.some(
          (type) => type.integrationType === 'PAYROLL_OUTPUT' && type.status === 'ACTIVE'
        )
    ) ?? false;

  const bulkActions = {
    download: { action: handleDownloadReports },
  };

  if (isLoading) {
    return (
      <Center width="full" height="full" flexGrow={1} py="40px">
        <Spinner color="brand.purple" size="xl" />
      </Center>
    );
  }

  return (
    <Flex flexDir="column" gap="20px" w="full">
      <Filters
        activeFilter={activeFilter}
        displayFormatter={formatFilterValue}
        filters={filters}
        onFilterClose={() => setActiveFilter(undefined)}
        onFilterChange={handleFilterChange}
        onFilterClick={setActiveFilter}
      />
      <Table
        columns={getDeductionsReportColumns(
          actions,
          hasActivePayrollOutputIntegration,
          view === 'global' && !isPayCalendarScoped
        )}
        data={deductionsReports?.list || []}
        total={deductionsReports?.total || 0}
        pageNumber={deductionsReports?.pageNumber || pageIndex}
        pageSize={deductionsReports?.pageSize || pageSize}
        pageSizeOptions={PAGE_SIZE_OPTIONS}
        onPaginationChange={setPagination}
        sorting={sorting}
        onSortingChange={setSorting}
        getRowId={(report) => report.id}
        rowSelection={rowSelection}
        onRowSelectionChange={setRowSelection}
        bulkActions={bulkActions}
        bulkActionsType="deductionsReport"
      />
      <RetryDeductionsReportDialog
        isOpen={reportIdPendingRetry !== undefined}
        isRetrying={isRetryingDeductionsReport}
        onClose={() => setReportIdPendingRetry(undefined)}
        onConfirm={handleConfirmRetry}
      />
      <DeductionsReportErrorsModal
        report={reportWithProblemsShown}
        onClose={() => setReportWithProblemsShown(undefined)}
      />
    </Flex>
  );
}

export default CompanyDeductionReportsTab;
